import { forwardRef } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { cn } from '../utils/cn';
import { useSpecular } from './useSpecular';

export type GlassTier = 1 | 2 | 3;
export type GlassRadius = 'sm' | 'md' | 'lg' | 'xl';

export const glassRadius: Record<GlassRadius, string> = {
  sm: 'var(--g-radius-sm)',
  md: 'var(--g-radius)',
  lg: 'var(--g-radius-lg)',
  xl: 'var(--g-radius-xl)',
};

/**
 * One focus treatment for the whole glass layer. An outline rather than a ring,
 * because `ring-offset` needs an opaque offset colour and there isn't one behind
 * a translucent pane.
 */
export const glassFocusRing =
  'outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--g-accent)]';

interface TierStyle {
  background: string;
  boxShadow: string;
  borderColor: string;
}

/**
 * Three depths, matching the three surface tokens. Tier 3 carries the brighter
 * edge and the strong specular because it is nearest the viewer — modals and
 * popovers — and the highlight is what reads as "closer".
 */
const tierStyles: Record<GlassTier, TierStyle> = {
  1: {
    background: 'var(--g-surface-1)',
    boxShadow: 'var(--g-shadow), var(--g-specular)',
    borderColor: 'var(--g-edge)',
  },
  2: {
    background: 'var(--g-surface-2)',
    boxShadow: 'var(--g-shadow-sm), var(--g-specular)',
    borderColor: 'var(--g-edge)',
  },
  3: {
    background: 'var(--g-surface-3)',
    boxShadow: 'var(--g-shadow-lg), var(--g-specular-strong)',
    borderColor: 'var(--g-edge-bright)',
  },
};

export interface GlassSurfaceProps extends HTMLMotionProps<'div'> {
  tier?: GlassTier;
  /** Cursor-tracking sheen. Reserve it for surfaces that respond to a click. */
  specular?: boolean;
  radius?: GlassRadius;
  /**
   * Opaque data surface. Glass belongs to the navigation layer; anything a user
   * reads carefully sits on this instead.
   */
  solid?: boolean;
  /** Inset matching the radius scale, so corners stay concentric. */
  padded?: boolean | GlassPadding;
}

export type GlassPadding = 'sm' | 'md' | 'lg';

const paddingPx: Record<GlassPadding, number> = { sm: 14, md: 20, lg: 28 };

export const GlassSurface = forwardRef<HTMLDivElement, GlassSurfaceProps>(function GlassSurface(
  {
    tier = 1,
    specular = false,
    radius = 'lg',
    solid = false,
    padded = false,
    className,
    style,
    onPointerMove,
    onPointerLeave,
    children,
    ...rest
  },
  ref,
) {
  const specularProps = useSpecular<HTMLDivElement>(specular && !solid);
  const material = tierStyles[tier];

  return (
    <motion.div
      ref={ref}
      className={cn(
        solid ? 'g-solid' : 'g-surface',
        specular && !solid && 'g-specular-hover',
        className,
      )}
      style={{
        borderRadius: glassRadius[radius],
        ...(padded ? { padding: paddingPx[padded === true ? 'lg' : padded] } : null),
        ...(solid ? null : material),
        ...style,
      }}
      onPointerMove={(event) => {
        specularProps.onPointerMove(event);
        onPointerMove?.(event);
      }}
      onPointerLeave={(event) => {
        specularProps.onPointerLeave(event);
        onPointerLeave?.(event);
      }}
      {...rest}
    >
      {children}
    </motion.div>
  );
});
